import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Hook for managing paginated, searchable, filterable lists.
 *
 * Features:
 * - Server-side pagination with configurable page size
 * - Server-side search (use SearchInput component for debouncing)
 * - Server-side sorting with multi-field support
 * - Filter state management with automatic page reset
 * - Loading and error states
 * - Automatic refetch on filter/search/sort changes
 * - Race condition protection via request IDs
 * - Graceful error handling (keeps old data on error)
 *
 * @param {string} endpoint - API endpoint path (e.g., '/api/parts/')
 * @param {object} options - Configuration options
 * @param {object} options.initialFilters - Initial filter values
 * @param {string} options.defaultSortField - Default field to sort by
 * @param {string} options.defaultSortDirection - 'asc' or 'desc'
 * @param {number} options.defaultPageSize - Initial page size (default: 50)
 * @param {boolean} options.enabled - Whether to fetch data (default: true)
 * @param {string} options.initialSearchQuery - Initial search query (e.g., from localStorage)
 *
 * @returns {object} Paginated list state and controls
 */
export const usePaginatedList = (endpoint, options = {}) => {
  const {
    initialFilters = {},
    defaultSortField = '',
    defaultSortDirection = 'asc',
    defaultPageSize = 50,
    enabled = true,
    initialSearchQuery = '',
  } = options;

  // Core state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  // Filter and search state
  const [filters, setFiltersState] = useState(initialFilters);
  const [searchQuery, setSearchQueryState] = useState(initialSearchQuery);
  const prevSearchQuery = useRef(searchQuery);
  const prevFilters = useRef(filters);

  // Shallow comparison for objects
  const shallowEqual = useCallback((obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (!obj1 || !obj2) return false;
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    for (const key of keys1) {
      if (obj1[key] !== obj2[key]) return false;
    }
    return true;
  }, []);

  // Wrapper for setFilters that skips if values unchanged (prevents double requests)
  const setFilters = useCallback((newFilters) => {
    setFiltersState((prev) => {
      const resolved = typeof newFilters === 'function' ? newFilters(prev) : newFilters;
      // Skip update if values are the same (shallow comparison)
      if (shallowEqual(prev, resolved)) {
        return prev;
      }
      return resolved;
    });
  }, [shallowEqual]);

  // Wrapper for setSearchQuery that skips if value unchanged
  const setSearchQuery = useCallback((newQuery) => {
    setSearchQueryState((prev) => {
      const resolved = typeof newQuery === 'function' ? newQuery(prev) : newQuery;
      return resolved === prev ? prev : resolved;
    });
  }, []);

  // Sorting state
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Request ID for race condition protection
  const requestIdRef = useRef(0);

  // Compute the effective page - reset to 1 if search/filters changed
  const effectivePage = useMemo(() => {
    // On search or filter change (not initial), reset to page 1
    const searchChanged = !isInitialMount.current && searchQuery !== prevSearchQuery.current;
    const filtersChanged = !isInitialMount.current && JSON.stringify(filters) !== JSON.stringify(prevFilters.current);

    if (searchChanged || filtersChanged) {
      return 1;
    }
    return page;
  }, [searchQuery, filters, page]);

  // Update refs after computing effective page
  useEffect(() => {
    prevSearchQuery.current = searchQuery;
    prevFilters.current = filters;
  }, [searchQuery, filters]);

  // Sync actual page state when effective page differs (after search/filter change)
  useEffect(() => {
    if (effectivePage !== page) {
      setPage(effectivePage);
    }
  }, [effectivePage, page]);

  // Build query params string - uses effectivePage to prevent stale page on search/filter change
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();

    params.set('page', String(effectivePage));
    params.set('page_size', String(pageSize));

    if (searchQuery) {
      params.set('search', searchQuery);
    }

    if (sortField) {
      const ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
      params.set('ordering', ordering);
    }

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      // Skip null, undefined, empty string, and 'all' values
      if (value === null || value === undefined || value === '' || value === 'all') {
        return;
      }

      // Handle arrays (multi-select filters)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(key, value.join(','));
        }
      } else {
        params.set(key, String(value));
      }
    });

    return params.toString();
  }, [effectivePage, pageSize, searchQuery, sortField, sortDirection, filters]);

  // Fetch data from server with race condition protection
  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    // Increment request ID and capture it for this request
    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    // Clear error only at the start of a new request
    setError(null);

    try {
      const url = queryParams ? `${endpoint}?${queryParams}` : endpoint;
      const response = await axios.get(url);

      // Ignore response if a newer request has been made (race condition protection)
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      // Handle both paginated and non-paginated responses
      if (response.data.results !== undefined) {
        // Paginated response
        setData(response.data.results);
        setTotalCount(response.data.count || 0);
      } else if (Array.isArray(response.data)) {
        // Non-paginated response (e.g., typeahead search)
        setData(response.data);
        setTotalCount(response.data.length);
      } else {
        // Single object or other response
        setData([response.data]);
        setTotalCount(1);
      }
    } catch (err) {
      // Ignore error if a newer request has been made (race condition protection)
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      console.error('Error fetching paginated list:', err);
      // Set error but DON'T wipe data - keep showing old data
      // This allows the UI to show an error message while preserving the previous state
      setError(err.response?.data?.detail || err.message || 'An error occurred');
      // Note: We intentionally don't setData([]) or setTotalCount(0) here
      // to keep the previous data visible during transient errors
    } finally {
      // Only update loading state if this is still the current request
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        isInitialMount.current = false;
      }
    }
  }, [endpoint, queryParams, enabled]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update filters and reset to page 1
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  }, [setFilters]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setSearchQuery('');
    setPage(1);
  }, [initialFilters, setFilters, setSearchQuery]);

  // Handle sort field change
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1);
  }, [sortField]);

  // Set sort directly
  const setSort = useCallback((field, direction = 'asc') => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / pageSize) || 1;
  }, [totalCount, pageSize]);

  return {
    // Data
    data,
    loading,
    error,

    // Pagination
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize: handlePageSizeChange,

    // Search
    searchQuery,
    setSearchQuery,

    // Filters
    filters,
    updateFilters,
    resetFilters,
    setFilters,

    // Sorting
    sortField,
    sortDirection,
    handleSort,
    setSort,

    // Refetch
    refetch: fetchData,
  };
};

export default usePaginatedList;
